import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Box, Typography } from "@mui/material"
import PriceService from "src/services/price-service"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import {
    BONDS_PRICE_COLUMNS,
    createBondsPriceRows,
} from "./tabela-preco-bonds-columns"

const TABLE_ERROR_MESSAGE = "Não foi possível carregar os preços de Bonds."

const TabelaPrecoBonds = ({ date, setLoading }) => {
    const priceService = useMemo(() => new PriceService(), [])
    const [rows, setRows] = useState([])
    const [status, setStatus] = useState("loading")
    const [retryVersion, setRetryVersion] = useState(0)

    useEffect(() => {
        let ignoreResponse = false

        const loadBondsPrices = async () => {
            setStatus("loading")
            setLoading(true)

            try {
                const bonds = await priceService.getBondsPrices(date)

                if (ignoreResponse) return

                const priceRows = createBondsPriceRows(bonds)
                setRows(priceRows)
                setStatus(priceRows.length === 0 ? "empty" : "success")
            } catch {
                if (ignoreResponse) return

                setRows([])
                setStatus("error")
                dispatchSnackbar(TABLE_ERROR_MESSAGE, "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadBondsPrices()

        return () => {
            ignoreResponse = true
        }
    }, [date, priceService, retryVersion, setLoading])

    if (status === "error") {
        return (
            <Box
                role="alert"
                aria-live="assertive"
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    textAlign: "center",
                }}
            >
                <Typography variant="textBody" sx={{ color: "error.light" }}>
                    {TABLE_ERROR_MESSAGE}
                </Typography>
                <ButtonWithIcon
                    size="md"
                    onClick={() => setRetryVersion((value) => value + 1)}
                >
                    Tentar novamente
                </ButtonWithIcon>
            </Box>
        )
    }

    return (
        <DataGridTable
            columns={BONDS_PRICE_COLUMNS}
            rows={rows}
            loading={status === "loading"}
            pageSize={50}
            resetKey={date}
            contained
            autoRowHeight
            rowPadding={2}
            disableRowSelectionOnClick
            sortingOrder={["asc", "desc"]}
            manualClientDataOperations
        />
    )
}

TabelaPrecoBonds.propTypes = {
    date: PropTypes.string.isRequired,
    setLoading: PropTypes.func.isRequired,
}

export default TabelaPrecoBonds
